/**
 * Export JSON básico — red de seguridad temprana (Ajuste 3 aprobado).
 *
 * La aplicación se empieza a usar de verdad después de la Fase 3, pero la
 * sincronización llega en la Fase 8. Entre medio, el progreso no puede depender
 * exclusivamente del almacenamiento del navegador.
 *
 * Esto NO es la pantalla completa de Backup (Fase 6): es un botón que baja todo
 * lo que hay, para que un desalojo de IndexedDB no borre semanas de trabajo.
 *
 * DOS REGLAS DURAS:
 *  - El JSON no lleva metadatos de sincronización (CA-4.8). Son detalle interno
 *    y filtrarlos rompería el formato para cualquier otro consumidor.
 *  - El JSON no lleva blobs de audio (CA-4.9), solo metadatos de grabación. Un
 *    respaldo de 60 MB en base64 no es un respaldo, es un problema.
 */

import type { Ajustes } from '../domain/modelo/ajustes.js';
import type { ElementoBanco } from '../domain/modelo/banco.js';
import type { Grabacion } from '../domain/modelo/grabacion.js';
import type { SesionRegistrada } from '../domain/modelo/sesion.js';
import { sinMetadatosSync } from '../domain/modelo/sincronizacion.js';
import { ajustesRepo } from './ajustesRepo.js';
import { bancoRepo } from './bancoRepo.js';
import { grabacionesRepo } from './grabacionesRepo.js';
import { sesionesRepo } from './sesionesRepo.js';

/**
 * Versión del FORMATO del archivo exportado.
 *
 * Eje de versión independiente de la versión de esquema de Dexie y de la
 * versión del curriculum. Bumpear uno no implica bumpear los otros.
 */
export const VERSION_EXPORT = 1;

export interface ArchivoExportado {
  formato: 'english-training-hub';
  versionFormato: number;
  exportadoEn: string;
  sesiones: unknown[];
  banco: unknown[];
  grabaciones: unknown[];
  ajustes: Ajustes;
}

/** Quita el blob: el JSON lleva metadatos de grabación, nunca el audio. */
function grabacionExportable(grabacion: Grabacion): unknown {
  const { blobLocal: _blob, ...resto } = grabacion;
  return sinMetadatosSync(resto);
}

export async function construirExport(): Promise<ArchivoExportado> {
  const [sesiones, banco, grabaciones, ajustes] = await Promise.all([
    sesionesRepo.listar(),
    bancoRepo.listar(),
    grabacionesRepo.listar(),
    ajustesRepo.obtener(),
  ]);

  return {
    formato: 'english-training-hub',
    versionFormato: VERSION_EXPORT,
    exportadoEn: new Date().toISOString(),
    sesiones: sesiones.map((s: SesionRegistrada) => sinMetadatosSync(s)),
    banco: banco.map((e: ElementoBanco) => sinMetadatosSync(e)),
    grabaciones: grabaciones.map(grabacionExportable),
    ajustes,
  };
}

export function nombreDeArchivo(ahora: Date = new Date()): string {
  const fecha = ahora.toISOString().slice(0, 10);
  return `english-training-hub-${fecha}.backup.json`;
}

/**
 * Descarga el respaldo.
 *
 * Usa un `blob:` URL y un `<a download>` sintético, que es lo que funciona en
 * Chrome Android dentro de una PWA instalada.
 */
export async function descargarExport(): Promise<string> {
  const datos = await construirExport();
  const json = JSON.stringify(datos, null, 2);
  const nombre = nombreDeArchivo();

  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  try {
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = nombre;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
  } finally {
    // Revocar de inmediato cancelaría la descarga en algunos navegadores.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  return nombre;
}

/** Cuántos registros hay en total, para saber si vale la pena exportar. */
export async function contarRegistros(): Promise<number> {
  const datos = await construirExport();
  return datos.sesiones.length + datos.banco.length + datos.grabaciones.length;
}
